import { useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { formatCurrency } from '@opendoor/shared-fe/formatUtils';
import type { useTouring } from 'components/self-tour/hooks/useTouring';

import type { MarketplaceProperty } from '../types';
import { PDP_SAVINGS_ARROW_URL } from './media';
import { PdpMortgageEstimate } from './PdpMortgageEstimate';
import type { MortgageQuoteRefresh } from './PdpMortgageEstimate';
import { TourActionButton } from './PdpTourActionButton';

export const PdpPurchaseCard = ({
  property,
  touring,
}: {
  property: MarketplaceProperty;
  touring: NonNullable<ReturnType<typeof useTouring>> | null;
}) => {
  const [refreshedQuote, setRefreshedQuote] = useState<MortgageQuoteRefresh>();
  const offer = refreshedQuote ? refreshedQuote.offer : property.mortgageOffer;
  const estimate =
    offer?.products.find((product) => product.isPromotionalRate) ??
    offer?.products.find((product) => product.product === 'THIRTY_YEAR_FIXED') ??
    offer?.products[0];
  const price = touring ? (property.buyDirectPrice ?? property.listPrice) : property.listPrice;

  const priceSection = (
    <Col className="w-full gap-4">
      <Col className="w-full gap-2.5">
        <Text className="flex h-5 items-center text-body-sm text-od-content-secondary">
          {touring ? 'Buy with Opendoor' : 'List price'}
        </Text>
        <Row className="w-full flex-wrap items-center gap-x-4 gap-y-2">
          <Text
            as="h2"
            className="whitespace-nowrap text-subheader-lg @[18rem]:text-header-sm @[24rem]:text-header-lg text-od-content-primary"
          >
            {price == null ? 'Price not available' : formatCurrency(price)}
          </Text>
          {touring && property.listPrice != null && price != null && price < property.listPrice && (
            <Row className="shrink-0 items-center gap-2 rounded-lg bg-od-bg-success/10 px-2 py-1 text-od-content-success">
              <Box
                aria-hidden
                className="size-4 shrink-0 items-center justify-center rounded-full bg-od-bg-success"
              >
                <Image src={PDP_SAVINGS_ARROW_URL} alt="" width={12} height={12} />
              </Box>
              <Text className="whitespace-nowrap text-label-sm">
                <Text as="span" className="sr-only">
                  List price{' '}
                </Text>
                <Text as="span" className="line-through">
                  {formatCurrency(property.listPrice)}
                </Text>
              </Text>
            </Row>
          )}
        </Row>
      </Col>
      {touring && (
        <TourActionButton
          action={touring.primaryAction}
          variant={estimate ? 'secondary' : 'primary'}
          className="h-12 w-full px-3 text-label-sm"
        />
      )}
    </Col>
  );
  const mortgageSection = offer && estimate && (
    <PdpMortgageEstimate
      key={`${property.addressToken}:${offer.quoteId}:${estimate.product}`}
      addressId={property.addressToken}
      offer={offer}
      estimate={estimate}
      onQuoteRefresh={setRefreshedQuote}
      useGenericApplication={
        estimate.product !== 'THIRTY_YEAR_FIXED' || refreshedQuote?.canRetryQuote === false
      }
    />
  );

  return (
    <Col
      as="section"
      aria-label="Home price"
      className="@container w-full gap-6 rounded-2xl bg-od-bg-primary p-6 ring-1 ring-od-border-primary shadow-lg shadow-od-bg-scrim/5"
    >
      {touring ? mortgageSection : priceSection}
      {mortgageSection && (
        <Box
          aria-hidden
          className="relative h-0 w-full before:absolute before:inset-x-0 before:bottom-0 before:h-px before:bg-od-border-primary before:content-['']"
        />
      )}
      {touring ? priceSection : mortgageSection}
      {refreshedQuote && (
        <Text role="alert" className="text-body-sm text-od-content-error">
          {refreshedQuote.message}
        </Text>
      )}
    </Col>
  );
};
